import { getVulnerabilityMap, getVulnerabilityLabel } from '../../utils/vulnerabilityMapper';
import type { Position } from '../../types/problems';

/**
 * The vulnerability board from `VulnerabilityBoard.jpg` (feature 023, FR-030).
 *
 * A compass diamond: four triangles meeting at the centre, with the vulnerable
 * pair's quadrants filled red. The dealer's seat letter is ringed. Below the
 * board sits the pair caption ("N-S Vulnerable") and the dealer legend.
 *
 * Display-only — it derives everything from the SAME `getVulnerabilityMap` the
 * old `VulnerabilityIndicator` uses, so the two can never disagree while both
 * are on screen during the migration.
 *
 * Accessibility: the drawing is `aria-hidden` and the whole board carries a
 * single text alternative, because four coloured triangles convey one fact and
 * announcing them individually would be noise. The caption and legend are real
 * text, so the information is never colour-only (WCAG 1.4.1).
 */

export interface VulnerabilityBoardProps {
  /** Raw vulnerability value, as stored on the problem/hand. */
  vulnerability: string;
  /** Seat the question is asked from — decides Favorable/Unfavorable framing. */
  decisionSeat?: Position;
  /** Dealer's seat; ringed on the board when supplied. */
  dealer?: Position;
}

const SEAT_NAMES: Record<Position, string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

/** Fill for a vulnerable quadrant — 6.1:1 against the white letter on top. */
const VULN_FILL = '#b02a2a';
const SAFE_FILL = '#fbf7ec';

/**
 * Caption naming the vulnerable PAIR. No longer rendered — the heading carries the
 * seat-relative wording instead (client decision, 2026-07-21) — but kept because the
 * board's text alternative still needs to state which pair is vulnerable, and colour
 * alone would not (WCAG 1.4.1).
 */
function pairCaption(nsVulnerable: boolean, ewVulnerable: boolean): string {
  if (nsVulnerable && ewVulnerable) return 'Both Vulnerable';
  if (!nsVulnerable && !ewVulnerable) return 'None Vulnerable';
  return nsVulnerable ? 'N-S Vulnerable' : 'E-W Vulnerable';
}

/**
 * The board's heading: the seat-relative state, which is what the learner actually acts on.
 *
 * The known states stand alone — "Favorable", "Unfavorable", "Neither", "Both" — because the
 * board directly beneath them already says what they are about. Only the null case spells out
 * "Unknown Vulnerability", where the bare word "Unknown" would not say unknown WHAT.
 *
 * `getVulnerabilityLabel` returns null for a value it cannot interpret; that must never read
 * as "Neither", which would be a lie about the board.
 */
export function vulnerabilityHeading(label: string | null): string {
  return label ?? 'Unknown Vulnerability';
}

export function VulnerabilityBoard({
  vulnerability,
  decisionSeat = 'S',
  dealer,
}: VulnerabilityBoardProps) {
  const map = getVulnerabilityMap(vulnerability, decisionSeat);
  const caption = pairCaption(map.N, map.E);
  const seatLabel = getVulnerabilityLabel(vulnerability, decisionSeat);

  // Triangle geometry on a 120×120 viewBox: the diamond occupies the central 68×68 and each
  // quadrant runs from two adjacent corners to the centre. The margin round it exists so the
  // dealer's "D" can sit OUTSIDE the coloured area, the way the old indicator placed it —
  // inside the quadrant it collided with the seat letter.
  //
  // The channel was widened 16 → 22 on 2026-09-04, when the letters moved out of the SVG: drawn
  // as SVG `<text fontSize="13">` they scaled with the viewBox and PAINTED at 6.5px on a 60px
  // board, half the constitution's 12px floor, while the computed-style sweep read 13px and
  // passed them. As HTML text they are sized in real CSS pixels, which is both the floor met and
  // the floor measurable.
  //
  // Widened again 22 → 26 on 2026-09-07 (client report: the board reads cramped round its
  // letters). 22 units is 11px of channel on the 60px board of the time — a 12px "D" filled it
  // edge to edge — and 19px on the desktop board, where the glyph still sat 3px off the
  // diamond. At 26 units the channel is 14.7px on the smallest board this now uses (68px) and
  // 26px on the desktop 120px board, so the "D" clears the coloured area at every size. The
  // diamond gives up 4 more units a side; it still carries its one fact — which pair is
  // vulnerable — at 68 units across.
  //
  // Percentages, not viewBox units, for the HTML layer: they hold at every board size without a
  // second set of numbers to keep in step. Dealer marks sit at the centre of the channel outside
  // the diamond.
  //
  // Seat letters sit at 33.5%, which is the other half of the same complaint. The centroid the
  // board used (31.11%) is a third of the way down a triangle that is widest at its outer edge,
  // so the letter hugged that edge — 0.3px of clearance on the 60px board — while leaving twice
  // as much room towards the centre. The obvious correction, the midpoint of the triangle's
  // height, overshoots: it drives all four letters into the centre vertex where they meet each
  // other, which is worse crowding than the edge because there are three other glyphs there and
  // only quiet channel at the edge. 33.5% is between the two, and was picked by rendering all
  // three at the smallest and the reference board in both the Both-Vulnerable and the N-S case.
  // It clears the coloured edge by ~2px on the smallest board and ~7px on the reference one, and
  // keeps the centre open.
  const TRIANGLES: Array<{
    seat: Position;
    points: string;
    /** Where the seat letter sits, as % of the board box. See the note above. */
    label: [number, number];
    /** Centre of the outer channel on that seat's side, as % of the board box. */
    dealerMark: [number, number];
  }> = [
    { seat: 'N', points: '26,26 94,26 60,60', label: [50, 33.5], dealerMark: [50, 10.83] },
    { seat: 'E', points: '94,26 94,94 60,60', label: [66.5, 50], dealerMark: [89.17, 50] },
    { seat: 'S', points: '26,94 94,94 60,60', label: [50, 66.5], dealerMark: [50, 89.17] },
    { seat: 'W', points: '26,26 26,94 60,60', label: [33.5, 50], dealerMark: [10.83, 50] },
  ];

  const vulnerableSeats = (Object.keys(map) as Position[]).filter((s) => map[s]);
  const description =
    `Vulnerability: ${caption}.` +
    (vulnerableSeats.length ? ` Vulnerable seats: ${vulnerableSeats.map((s) => SEAT_NAMES[s]).join(' and ')}.` : '') +
    (dealer ? ` Dealer is ${SEAT_NAMES[dealer]}.` : '');

  return (
    <div className="flex flex-col items-center" data-testid="vulnerability-board">
      {/* Capped at the board's own width so a multi-word heading wraps at every viewport
          size, not just narrow ones: unwrapped, "Unknown Vulnerability" runs ~170px wide
          and stretches this column, squeezing the hand beside it into wrapped, misaligned
          suit rows. Single-word headings ("Favorable", "Neither", …) are unaffected, and
          max-w (not a fixed w) lets the box grow rather than clip if a word measures wider
          than the board on some font stack. */}
      <h3
        // 12px floor, sentence case on phones (constitution VI, amended 2026-07-29). Up from
        // 10.5px, which was the smallest text on the table and carries a fact — favorable or
        // not — that changes how the hand is bid.
        className="mb-1.5 max-w-[120px] text-center text-xs font-extrabold uppercase tracking-[0.14em] text-ink-soft narrow:normal-case narrow:tracking-normal"
        data-testid="vulnerability-heading"
      >
        {vulnerabilityHeading(seatLabel)}
      </h3>

      {/* The board is a positioned BOX now, not a bare svg: the svg paints the quadrants and the
          seat letters ride above it as HTML text, so their size is CSS pixels rather than viewBox
          units. Sizing, border and background move here from the svg; `vuln-board` keeps its
          meaning, and index.css keeps sizing the board from that class. */}
      <div
        // ONE number per band, not a height/width pair: `--vuln-board-size` is what index.css
        // sets per viewport (`.vuln-board.vuln-board { --vuln-board-size: … }`), and the base
        // rule there turns it into the box's height and width AND into the letters' font-size,
        // so a band that wants a bigger board gets bigger letters with it and no second set of
        // media queries to keep in step. The floor is held in the CSS, not here: the letters
        // never resolve below 12px however small the board goes.
        //
        // Phones shrink the board so the stacked table-frame panels fit the viewport without
        // scrolling — the fact it carries is WHICH pair is vulnerable, and that is read off two
        // red quadrants at any size. Every size stepped up on 2026-09-07 (client: the board is
        // too tight around its letters), the reference board 104 → 120px. `vuln-board`: a
        // landscape phone is wider than the sm: breakpoint, so sm: would restore the reference
        // board on the very viewports with the least height to spare — index.css sizes those
        // from the class instead. The quadrants scale with the box (the svg's viewBox does
        // that); the letters scale with it only down to the 12px floor.
        className="vuln-board relative rounded-lg border border-line-strong bg-card [--vuln-board-size:84px] sm:[--vuln-board-size:120px] xnarrow:[--vuln-board-size:72px]"
        role="img"
        aria-label={description}
      >
        <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full" aria-hidden="true">
          {TRIANGLES.map(({ seat, points }) => (
            <polygon
              key={seat}
              points={points}
              fill={map[seat] ? VULN_FILL : SAFE_FILL}
              stroke="#e2d9bf"
              strokeWidth="0.75"
            />
          ))}
        </svg>

        {/* The letters. `aria-hidden` because the board already carries the whole fact in its
            `aria-label` — vulnerable pair and dealer, in words — and four loose capitals read
            after it would be noise, not information. Hiding them from assistive technology does
            NOT buy an exemption from the text-size floor (that wants no reading load either, and
            these plainly carry one); they meet it outright, which is the point of moving them
            here. `vuln-board-letter` sizes them from the board (index.css) and never resolves
            below 12px. */}
        {TRIANGLES.map(({ seat, label, dealerMark }) => (
          <span key={seat} aria-hidden="true">
            <span
              className="vuln-board-letter absolute font-bold leading-none"
              style={{
                left: `${label[0]}%`,
                top: `${label[1]}%`,
                transform: 'translate(-50%, -50%)',
                // White inside a vulnerable (red) quadrant, ink inside a neutral one.
                color: map[seat] ? '#ffffff' : '#22221d',
              }}
            >
              {seat}
            </span>
            {/* The dealer marker: a "D" pinned to that seat's outer channel. It replaces the ring
                the board used to draw round the seat letter, which read as "this seat is
                highlighted" without ever saying why. Always ink — it sits in the card-coloured
                channel, not on the quadrant, so white would vanish. */}
            {dealer === seat && (
              <span
                className="vuln-board-letter absolute font-bold leading-none"
                style={{
                  left: `${dealerMark[0]}%`,
                  top: `${dealerMark[1]}%`,
                  transform: 'translate(-50%, -50%)',
                  color: '#22221d',
                }}
                data-testid="vulnerability-dealer-mark"
              >
                D
              </span>
            )}
          </span>
        ))}
      </div>

      {/* The pair caption, the "Favorable for <seat>" line and the dealer legend all used
          to sit here. They are gone by client decision (2026-07-21): the heading now says
          the same thing in one line, and the hand's own vulnerability bar says it again
          where the learner is already looking. Everything they carried survives in the
          board's text alternative above, so nothing is lost to a screen reader. */}
    </div>
  );
}
